/**
 * Export service — ZIP export and cloud save.
 * Replaces window.ExportService.
 */
import * as renderService from './renderService';
import * as dataService from './dataService';
import type { Product, FileItem, PrintItem, CloudProgress } from '../stores/appStore';
import type { Transform } from '../stores/types';
import { loadImage } from '../utils';

/** Export mockups to ZIP file */
export async function exportToZip(
  selectedPrint: FileItem | PrintItem,
  products: Product[],
  transforms: Record<string, Transform>,
  productTransforms: Record<string, Transform>,
  activeTab: string,
): Promise<Blob> {
  const { default: JSZip } = await import('jszip');
  if (!selectedPrint) throw new Error('Выберите картинку');

  const zip = new JSZip();
  const exportMode = activeTab === 'products' ? 'products' : 'mockups';
  const printUrl = 'url' in selectedPrint ? selectedPrint.url : '';
  const printImg = await loadImage(printUrl);
  if (!printImg) throw new Error('Не удалось загрузить принт');

  const enabledProducts = products.filter((p) => p.enabled);

  const renderTasks = enabledProducts.map(async (prod) => {
    const tr = renderService.getTransformByMode(
      transforms,
      productTransforms,
      exportMode,
      prod.id,
      exportMode === 'products' ? 0.6 : 0.5,
    );
    const productDPI = prod.dpi || 300;

    let targetWidth: number,
      targetHeight: number,
      targetMask: string | undefined,
      targetOverlay: string | undefined;
    if (exportMode === 'products') {
      targetWidth = prod.width;
      targetHeight = prod.height;
      targetMask = prod.mask;
      targetOverlay = prod.overlay;
    } else {
      targetWidth = prod.mockupWidth || prod.width;
      targetHeight = prod.mockupHeight || prod.height;
      targetMask = prod.mockupMask || prod.mask;
      targetOverlay = prod.mockupOverlay || prod.overlay;
    }

    const blob = await renderService.renderMockupBlob(
      prod,
      printImg,
      tr,
      productDPI,
      targetWidth,
      targetHeight,
      {
        mimeType: 'image/png',
        maskUrl: targetMask,
        overlayUrl: targetOverlay,
        renderRotation: prod.renderRotation,
      },
    );
    if (!blob) return null;

    const safeName = (selectedPrint.name || '').split('.')[0];
    const prefix = prod.defaultPrefix || prod.name;
    return { fileName: `${prefix}-${safeName}.png`, blob };
  });

  const results = await Promise.all(renderTasks);
  results.forEach((r) => {
    if (r) zip.file(r.fileName, r.blob);
  });
  return zip.generateAsync({ type: 'blob' });
}

/** Save mockups to cloud */
export async function saveToCloud(
  selectedPrint: FileItem | PrintItem,
  products: Product[],
  transforms: Record<string, Transform>,
  productTransforms: Record<string, Transform>,
  activeTab: string,
  cloudMode: string,
  onProgress?: (p: CloudProgress | ((prev: CloudProgress) => CloudProgress)) => void,
): Promise<void> {
  if (!selectedPrint) throw new Error('Выберите принт для сохранения');

  const modeToUse = activeTab === 'base' ? cloudMode : activeTab;
  const printUrl = 'url' in selectedPrint ? selectedPrint.url : '';
  const printImg = await loadImage(printUrl);
  if (!printImg) throw new Error('Не удалось загрузить принт');

  const enabledProducts = products.filter((p) => p.enabled);
  if (enabledProducts.length === 0) throw new Error('Нет включенных товаров для сохранения');

  const article =
    ('article' in selectedPrint ? selectedPrint.article : null) ||
    (selectedPrint.name || '').split('.')[0];
  const categoryFolder = modeToUse === 'products' ? 'products' : 'mockups';

  if (onProgress) onProgress({ total: enabledProducts.length, done: 0, current: '' });

  // Phase 1: Parallel rendering
  const renderTasks = enabledProducts.map(async (prod) => {
    const tr = renderService.getTransformByMode(
      transforms,
      productTransforms,
      modeToUse,
      prod.id,
      modeToUse === 'products' ? 0.6 : 0.5,
    );
    const productDPI = prod.dpi || 300;

    let targetWidth: number,
      targetHeight: number,
      targetMask: string | undefined,
      targetOverlay: string | undefined;
    if (modeToUse === 'products') {
      targetWidth = prod.width;
      targetHeight = prod.height;
      targetMask = prod.mask;
      targetOverlay = prod.overlay;
    } else {
      targetWidth = prod.mockupWidth || prod.width;
      targetHeight = prod.mockupHeight || prod.height;
      targetMask = prod.mockupMask || prod.mask;
      targetOverlay = prod.mockupOverlay || prod.overlay;
    }

    const blob = await renderService.renderMockupBlob(
      prod,
      printImg,
      tr,
      productDPI,
      targetWidth,
      targetHeight,
      {
        mimeType: 'image/png',
        maskUrl: targetMask,
        overlayUrl: targetOverlay,
        renderRotation: prod.renderRotation,
      },
    );
    if (!blob) return null;

    const prefix = prod.defaultPrefix || prod.name;
    return { prod, blob, fileName: `${prefix}-${article}.png` };
  });

  const results = await Promise.all(renderTasks);

  // Phase 2: Sequential upload
  let uploaded = 0;
  for (const result of results) {
    if (!result) continue;
    if (onProgress) onProgress((prev) => ({ ...prev, current: result.prod.name, done: uploaded }));

    const printName =
      ('printName' in selectedPrint ? selectedPrint.printName : null) ||
      ('print_name' in selectedPrint ? selectedPrint.print_name : null) ||
      selectedPrint.name ||
      '';

    await dataService.uploadToCloud(
      result.blob,
      result.fileName,
      article,
      categoryFolder,
      printName,
      result.prod.name,
      selectedPrint.name,
    );
    uploaded++;
  }

  if (onProgress) onProgress((prev) => ({ ...prev, done: uploaded }));
}
